import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Building2, Loader2 } from 'lucide-react';
import { Button, Alert } from '@agion/ui';
import { Field } from '@/components/common/Field';

interface OrganizationSignupFormData {
  name: string;
  slug: string;
  description: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  requireMFA: boolean;
}

const generateSlug = (name: string) => {
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const OrganizationSignupForm: React.FC<{ loginChallenge?: string }> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailParam = searchParams.get('email') ?? '';
  const nameParam = searchParams.get('name') ?? '';
  const orgNameParam = nameParam ? (nameParam.split(' ')[0] ?? nameParam) : '';

  const [formData, setFormData] = useState<OrganizationSignupFormData>({
    name: orgNameParam,
    slug: orgNameParam ? generateSlug(orgNameParam) : '',
    description: '',
    website: '',
    contactEmail: emailParam,
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    plan: 'free',
    requireMFA: false,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof OrganizationSignupFormData, string>>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof OrganizationSignupFormData, string>> = {};

    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'Organization name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Auto-generate slug from name
    if (name === 'name' && !formData.slug) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value),
      }));
    }

    // Clear error for this field
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const email = searchParams.get('email') || '';
      const domain = searchParams.get('domain') || '';

      // Call the public organization creation endpoint
      const response = await fetch('/api/v1/organizations/public', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          contact_email: email,
          domain: domain,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create organization');
      }

      const data = await response.json();

      if (data.success) {
        // Organization created - automatically redirect to Microsoft login to complete authentication
        window.location.href = '/v1/auth/microsoft/login';
      } else {
        setError(data.error || 'Failed to create organization');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="organization-signup-card">
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading" data-testid="form-title">{t('organizationSignup.title')}</h2>
        <p className="lp-subheading" data-testid="form-description">
          {t('organizationSignup.description')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="lp-form lp-rise lp-rise-3" data-testid="organization-signup-form">
        {error && (
          <Alert variant="error" data-testid="error-alert">
            {error}
          </Alert>
        )}

        <Field
          id="name"
          name="name"
          type="text"
          label="Organization Name *"
          value={formData.name}
          onChange={handleInputChange}
          placeholder={t('organizationSignup.namePlaceholder')}
          error={errors.name}
          required
          leadingIcon={<Building2 size={16} />}
          data-testid="organization-name-input"
        />

        <Button
          type="submit"
          disabled={isLoading}
          className="lp-btn-primary w-full"
          data-testid="submit-button"
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin" size={16} data-testid="loading-spinner" />
              {t('organizationSignup.creating')}
            </>
          ) : (
            <>
              {t('organizationSignup.submit')}
              <ArrowRight size={16} aria-hidden="true" />
            </>
          )}
        </Button>
      </form>

      <p className="lp-secondary-cta lp-rise lp-rise-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate('/login')}
          className="lp-link-btn"
          data-testid="back-to-login-button"
        >
          {t('organizationSignup.backToLogin')}
        </Button>
      </p>
    </div>
  );
};

export default OrganizationSignupForm;
